import { Dialog } from "@mui/material"
import type { FC } from "react"
import { useState } from "react"

import { useDeleteDraftQuoteRequest } from "../../hooks/useDeleteDraftQuoteRequest"
import { useSubmitQuoteRequest } from "../../hooks/useSubmitQuoteRequest"
import type { AddedProduct, NewRequestFormValues, SalesCustomer, SalesRequester } from "../../types"
import { NewRequestStep } from "../../types"
import { formatRequestIdSuffix } from "../../utils/format"

import { CustomerSearchStep } from "./CustomerSearchStep"
import type { PricingFormValues } from "./pricing/schemas"
import { PricingPath } from "./pricing/types"
import { ProductSearchStep } from "./ProductSearchStep"
import { RequesterStep } from "./RequesterStep"
import { ReviewRequestStep } from "./ReviewRequestStep"
import { SendStep } from "./SendStep"

import CancelDialog from "@/components/common/Modals/CancelDialog"
import DialogHeader from "@/components/common/Modals/DialogHeader"

interface NewRequestDialogProps {
  fullScreen?: boolean
  onClose: () => void
  onSubmit?: () => void
}

interface FinalizedQuoteData {
  customer: SalesCustomer
  requesters: SalesRequester[]
  products: AddedProduct[]
  pricingByProductId: Map<number, PricingFormValues>
}

type DialogState =
  | { step: NewRequestStep.Customer }
  | { step: NewRequestStep.Requester; customer: SalesCustomer }
  | {
      step: NewRequestStep.Product
      customer: SalesCustomer
      requesters: SalesRequester[]
      products?: AddedProduct[]
      pricingByProductId?: Map<number, PricingFormValues>
    }
  | ({ step: NewRequestStep.Review } & FinalizedQuoteData)
  | ({ step: NewRequestStep.SendToCustomer; quoteGroupId: number } & FinalizedQuoteData)

const toFormValues = (data: FinalizedQuoteData): NewRequestFormValues => ({
  customer: data.customer,
  requesters: data.requesters,
  products: data.products,
  pricingByProductId: data.pricingByProductId,
})

const getApprovalSuccessMessage = (ids: string[]) => `Quote sent for approval! ${formatRequestIdSuffix(ids)}`

export const NewRequestDialog: FC<NewRequestDialogProps> = ({ fullScreen = false, onClose, onSubmit }) => {
  const [state, setState] = useState<DialogState>({ step: NewRequestStep.Customer })
  const [isConfirmCancelOpen, setIsConfirmCancelOpen] = useState(false)
  const { submitQuoteRequest, isSubmitting } = useSubmitQuoteRequest()
  const { deleteDraftQuoteRequest } = useDeleteDraftQuoteRequest()

  const handleCancel = () => setIsConfirmCancelOpen(true)

  const handleCancelConfirm = () => {
    if (state.step === NewRequestStep.SendToCustomer) {
      void deleteDraftQuoteRequest(state.quoteGroupId)
    }
    onClose()
  }

  const handleCustomerContinue = (customer: SalesCustomer) => {
    setState({ step: NewRequestStep.Requester, customer })
  }

  const handleRequesterAndLocationsContinue = (requesters: SalesRequester[]) => {
    setState((prev) => {
      if (prev.step !== NewRequestStep.Requester) return prev
      return { step: NewRequestStep.Product, customer: prev.customer, requesters }
    })
  }

  const handleProductsContinue = (products: AddedProduct[], pricingByProductId: Map<number, PricingFormValues>) => {
    setState((prev) => {
      if (prev.step !== NewRequestStep.Product) return prev
      return {
        step: NewRequestStep.Review,
        customer: prev.customer,
        requesters: prev.requesters,
        products,
        pricingByProductId,
      }
    })
  }

  const handleReviewBack = () => {
    setState((prev) => {
      if (prev.step !== NewRequestStep.Review) return prev
      return {
        step: NewRequestStep.Product,
        customer: prev.customer,
        requesters: prev.requesters,
        products: prev.products,
        pricingByProductId: prev.pricingByProductId,
      }
    })
  }

  const submitForApproval = async (data: FinalizedQuoteData) => {
    const result = await submitQuoteRequest({
      formValues: toFormValues(data),
      emailReceiverContactIds: [],
      emailNote: "",
      getSuccessMessage: getApprovalSuccessMessage,
    })
    if (result.success) {
      onSubmit?.()
    }
  }

  // Creates the quote as a DRAFT bundle so a real quoteGroupId exists for the
  // Send-to-Customer step's Preview Email to query. If the user backs out or
  // cancels from there, the draft is soft-deleted (see handleSendBack /
  // handleCancelConfirm) rather than left as a visible empty opportunity.
  const continueToEmail = async (data: FinalizedQuoteData) => {
    const result = await submitQuoteRequest({
      formValues: toFormValues(data),
      emailReceiverContactIds: [],
      emailNote: "",
      getSuccessMessage: () => "",
      silent: true,
    })
    if (!result.success || result.quoteGroupId === undefined) return
    setState((prev) => {
      if (prev.step !== NewRequestStep.Review) return prev
      return { ...prev, step: NewRequestStep.SendToCustomer, quoteGroupId: result.quoteGroupId! }
    })
  }

  const handleReviewContinue = () => {
    if (state.step !== NewRequestStep.Review) return
    const hasSpecialLine = Array.from(state.pricingByProductId.values()).some(
      (pricing) => pricing.path === PricingPath.Special
    )
    if (hasSpecialLine) {
      void submitForApproval(state)
      return
    }
    void continueToEmail(state)
  }

  const handleSendBack = () => {
    if (state.step !== NewRequestStep.SendToCustomer) return
    void deleteDraftQuoteRequest(state.quoteGroupId)
    setState((prev) => {
      if (prev.step !== NewRequestStep.SendToCustomer) return prev
      return { ...prev, step: NewRequestStep.Review }
    })
  }

  const dialogTitle =
    state.step === NewRequestStep.Review
      ? "Review Request"
      : state.step === NewRequestStep.SendToCustomer
        ? "Send to Customer"
        : "New Request"

  return (
    <Dialog
      open
      onClose={handleCancel}
      fullScreen={fullScreen}
      maxWidth='sm'
      fullWidth
      data-testid='sales.new-request-dialog'
    >
      {isConfirmCancelOpen ? (
        <CancelDialog onClose={() => setIsConfirmCancelOpen(false)} onConfirm={handleCancelConfirm} />
      ) : (
        <>
          <DialogHeader title={dialogTitle} onClose={handleCancel} />

          {state.step === NewRequestStep.Customer && (
            <CustomerSearchStep onCancel={handleCancel} onContinue={handleCustomerContinue} />
          )}

          {state.step === NewRequestStep.Requester && (
            <RequesterStep
              customer={state.customer}
              onCancel={handleCancel}
              onContinue={handleRequesterAndLocationsContinue}
            />
          )}

          {state.step === NewRequestStep.Product && (
            <ProductSearchStep
              formValues={{
                customer: state.customer,
                requesters: state.requesters,
                products: state.products,
                pricingByProductId: state.pricingByProductId,
              }}
              onCancel={handleCancel}
              onContinue={handleProductsContinue}
            />
          )}

          {state.step === NewRequestStep.Review && (
            <ReviewRequestStep
              formValues={toFormValues(state)}
              onBack={handleReviewBack}
              onContinue={handleReviewContinue}
              continueLabel={
                Array.from(state.pricingByProductId.values()).some((pricing) => pricing.path === PricingPath.Special)
                  ? "Submit for Approval"
                  : "Continue To Email"
              }
              continueDisabled={isSubmitting}
            />
          )}

          {state.step === NewRequestStep.SendToCustomer && (
            <SendStep
              formValues={toFormValues(state)}
              quoteGroupId={state.quoteGroupId}
              onBack={handleSendBack}
              onSubmitted={() => {
                onSubmit?.()
              }}
            />
          )}
        </>
      )}
    </Dialog>
  )
}
